import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PageHeader from '../../components/PageHeader/PageHeader';
import MarketingPlanningService from '../../services/MarketingPlanningService';
import EmailTemplateService from '../../services/EmailTemplateService';
import EmailListService from '../../services/EmailListService';
import { getCroppedImg, readFile } from '../../utils/ImageUtils';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { getPublicationStatus } from './publicationStatus';

const channels = ['Instagram', 'Email Marketing'];
const instagramContentTypes = ['Historia', 'Reel', 'Post', 'Carrousel'];
const instagramAspectRatios = [
  { value: '1:1', label: 'Cuadrado', description: 'Feed 1:1', ratio: 1 },
  { value: '4:5', label: 'Vertical', description: 'Feed 4:5', ratio: 4 / 5 },
  { value: '1.91:1', label: 'Horizontal', description: 'Feed 1.91:1', ratio: 1.91 },
  { value: '9:16', label: 'Historia/Reel', description: '9:16', ratio: 9 / 16 }
];
const instagramAspectRatiosByType = {
  Historia: ['9:16'],
  Reel: ['9:16'],
  Post: ['4:5', '1:1'],
  Carrousel: ['4:5', '1:1']
};
const contentTypeStyles = {
  Historia: { bg: '#fce7f3', color: '#9d174d', border: '#f9a8d4' },
  Reel: { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd' },
  Post: { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  Carrousel: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
  Email: { bg: '#ffedd5', color: '#9a3412', border: '#fdba74' }
};
const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const weekdays = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mié' },
  { value: 4, label: 'Jue' },
  { value: 5, label: 'Vie' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' }
];

const emptyCampaign = () => ({
  name: '',
  objective: '',
  concept: '',
  resources: '',
  startDate: toDateInput(new Date()),
  endDate: toDateInput(addDays(new Date(), 30)),
  status: 'active',
  generateSkeleton: true,
  rules: [
    { weekdays: [1, 3, 5], channel: 'Instagram', contentType: 'Post', time: '09:00' },
    { weekdays: [1, 2, 3, 4, 5, 6, 0], channel: 'Instagram', contentType: 'Historia', time: '15:00' }
  ]
});

const emptyItem = (date) => ({
  plannedDate: date,
  time: '09:00',
  channel: 'Instagram',
  contentType: 'Historia',
  title: '',
  objective: '',
  copy: '',
  notes: '',
  status: 'Sin contenido',
  assetKey: '',
  assetAspectRatio: '1:1',
  assetPreviewUrl: '',
  assetMimeType: '',
  assets: [],
  emailTemplateId: '',
  emailListId: '',
  emailSendId: null
});

const Planning = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [previewCampaigns, setPreviewCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [selectedDate, setSelectedDate] = useState(toDateInput(new Date()));
  const [viewDate, setViewDate] = useState(startOfMonth(new Date()));
  const [loading, setLoading] = useState(true);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [deleteCampaignOpen, setDeleteCampaignOpen] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [campaignForm, setCampaignForm] = useState(emptyCampaign());
  const [itemForm, setItemForm] = useState(emptyItem(toDateInput(new Date())));
  const [editingItemId, setEditingItemId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [topTab, setTopTab] = useState(0);
  const [viewMode, setViewMode] = useState(0);
  const [error, setError] = useState('');
  const [emailReferences, setEmailReferences] = useState({ templates: [], lists: [] });
  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [schedulingPublication, setSchedulingPublication] = useState(false);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await MarketingPlanningService.getCampaigns();
      setCampaigns(list);
      if (list.length) {
        const fullCampaign = await MarketingPlanningService.getCampaign(list[0].id);
        setActiveCampaign(fullCampaign);
        setSelectedDate(fullCampaign.startDate);
        setViewDate(startOfMonth(parseDate(fullCampaign.startDate)));
      } else {
        setActiveCampaign(null);
        setPreviewCampaigns([]);
      }
    } catch (err) {
      console.error('Error loading marketing planning campaigns:', err);
      setError('No se pudo cargar la planificación de marketing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const loadIntegrations = useCallback(async () => {
    setIntegrationsLoading(true);
    try {
      const list = await MarketingPlanningService.getIntegrations();
      setIntegrations(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading marketing integrations:', err);
      SnackbarUtils.error('No se pudieron cargar las integraciones');
    } finally {
      setIntegrationsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const redirectUri = window.localStorage.getItem('axium_meta_redirect_uri');
    if (!code || !state || !redirectUri) return;

    let cancelled = false;
    const finishConnection = async () => {
      setTopTab(2);
      setIntegrationsLoading(true);
      try {
        await MarketingPlanningService.connectIntegration('instagram', { code, state, redirectUri });
        if (cancelled) return;
        window.localStorage.removeItem('axium_meta_redirect_uri');
        const cleanUrl = `${window.location.origin}${window.location.pathname}`;
        window.history.replaceState({}, document.title, cleanUrl);
        SnackbarUtils.success('Instagram conectado con Meta');
        await loadIntegrations();
      } catch (err) {
        console.error('Error connecting Meta integration:', err);
        SnackbarUtils.error(getRequestErrorMessage(err, 'No se pudo completar la conexión con Meta'));
      } finally {
        if (!cancelled) setIntegrationsLoading(false);
      }
    };

    finishConnection();
    return () => {
      cancelled = true;
    };
  }, [loadIntegrations]);

  useEffect(() => {
    let mounted = true;
    const loadPreviewCampaigns = async () => {
      if (!campaigns.length) {
        setPreviewCampaigns([]);
        return;
      }
      try {
        const details = await Promise.all(campaigns.map((campaign) => MarketingPlanningService.getCampaign(campaign.id)));
        if (mounted) setPreviewCampaigns(details);
      } catch (err) {
        console.error('Error loading campaigns for feed preview:', err);
        if (mounted) setPreviewCampaigns(activeCampaign ? [activeCampaign] : []);
      }
    };

    loadPreviewCampaigns();
    return () => {
      mounted = false;
    };
  }, [campaigns, activeCampaign]);

  useEffect(() => {
    let mounted = true;
    const loadEmailReferences = async () => {
      try {
        const [templatesResponse, listsResponse] = await Promise.all([
          EmailTemplateService.getTemplates(1, 200, ''),
          EmailListService.getLists(1, 200, '')
        ]);
        if (!mounted) return;
        setEmailReferences({
          templates: extractEmailTemplates(templatesResponse),
          lists: extractEmailLists(listsResponse)
        });
      } catch (err) {
        console.error('Error loading email references:', err);
      }
    };

    loadEmailReferences();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedItems = useMemo(
    () => (activeCampaign?.items || []).filter((item) => item.plannedDate === selectedDate).sort(sortByTime),
    [activeCampaign, selectedDate]
  );

  const emailTemplateMap = useMemo(
    () => new Map(emailReferences.templates.map((template) => [String(template.id), template])),
    [emailReferences.templates]
  );
  const emailListMap = useMemo(
    () => new Map(emailReferences.lists.map((list) => [String(list.id), list])),
    [emailReferences.lists]
  );
  const stats = useMemo(() => getStats(activeCampaign?.items || []), [activeCampaign]);
  const monthStats = useMemo(() => {
    const monthItems = (activeCampaign?.items || []).filter((item) => {
      const date = parseDate(item.plannedDate);
      return date.getFullYear() === viewDate.getFullYear() && date.getMonth() === viewDate.getMonth();
    });
    return getStats(monthItems);
  }, [activeCampaign, viewDate]);
  const timelineStats = useMemo(() => getTimelineStats(activeCampaign), [activeCampaign]);
  const dayStats = useMemo(() => getStats(selectedItems), [selectedItems]);

  const handleSelectCampaign = async (campaignId) => {
    const fullCampaign = await MarketingPlanningService.getCampaign(campaignId);
    setActiveCampaign(fullCampaign);
    setSelectedDate(fullCampaign.startDate);
    setViewDate(startOfMonth(parseDate(fullCampaign.startDate)));
  };

  const refreshActiveCampaign = async (campaignId = activeCampaign?.id) => {
    if (!campaignId) return;
    const fullCampaign = await MarketingPlanningService.getCampaign(campaignId);
    setActiveCampaign(fullCampaign);
    setPreviewCampaigns((prev) => prev.map((campaign) => (campaign.id === fullCampaign.id ? fullCampaign : campaign)));
    setCampaigns((prev) => prev.map((campaign) => (campaign.id === fullCampaign.id ? { ...campaign, ...fullCampaign, items: [] } : campaign)));
  };

  const handleCreateCampaign = async () => {
    try {
      const payload = {
        ...campaignForm,
        rules: campaignForm.rules.map((rule) => ({
          ...rule,
          contentType: rule.channel === 'Email Marketing'
            ? 'Email'
            : (instagramContentTypes.includes(rule.contentType) ? rule.contentType : 'Post'),
          title: defaultRuleTitle(rule)
        }))
      };
      const created = await MarketingPlanningService.createCampaign(payload);
      setCampaignModalOpen(false);
      SnackbarUtils.success('Campaña de planificación creada');
      const list = await MarketingPlanningService.getCampaigns();
      setCampaigns(list);
      await handleSelectCampaign(created.id);
    } catch (err) {
      console.error('Error creating campaign:', err);
      SnackbarUtils.error('No se pudo crear la campaña');
    }
  };

  const handleDeleteCampaign = async () => {
    if (!activeCampaign) return;
    try {
      const deletedId = activeCampaign.id;
      await MarketingPlanningService.deleteCampaign(deletedId);
      setDeleteCampaignOpen(false);
      SnackbarUtils.success('Campaña eliminada');
      const list = await MarketingPlanningService.getCampaigns();
      setCampaigns(list);
      if (list.length) {
        const fullCampaign = await MarketingPlanningService.getCampaign(list[0].id);
        setActiveCampaign(fullCampaign);
        setSelectedDate(fullCampaign.startDate);
        setViewDate(startOfMonth(parseDate(fullCampaign.startDate)));
      } else {
        setActiveCampaign(null);
      }
    } catch (err) {
      console.error('Error deleting campaign:', err);
      SnackbarUtils.error('No se pudo eliminar la campaña');
    }
  };

  const openItemModal = (item = null) => {
    setEditingItemId(item?.id || null);
    setItemForm(item ? normalizeItemForm(item) : emptyItem(selectedDate));
    setItemModalOpen(true);
  };

  const handleSaveItem = async () => {
    if (!activeCampaign) return;
    try {
      const payload = toItemPayload(itemForm);
      if (editingItemId) {
        await MarketingPlanningService.updateItem(editingItemId, payload);
      } else {
        await MarketingPlanningService.createItem(activeCampaign.id, payload);
      }
      setSelectedDate(itemForm.plannedDate);
      setViewDate(startOfMonth(parseDate(itemForm.plannedDate)));
      setItemModalOpen(false);
      SnackbarUtils.success('Contenido guardado');
      await refreshActiveCampaign();
    } catch (err) {
      console.error('Error saving item:', err);
      SnackbarUtils.error('No se pudo guardar el contenido');
    }
  };

  const handleDeleteItem = async () => {
    if (!editingItemId) return;
    try {
      await MarketingPlanningService.deleteItem(editingItemId);
      setItemModalOpen(false);
      SnackbarUtils.success('Contenido eliminado');
      await refreshActiveCampaign();
    } catch (err) {
      console.error('Error deleting item:', err);
      SnackbarUtils.error('No se pudo eliminar el contenido');
    }
  };

  const handleSchedulePublication = async () => {
    if (!activeCampaign) return;

    setSchedulingPublication(true);
    try {
      // Persist the latest edition before validation and scheduling it.
      const payload = toItemPayload(itemForm);
      const savedItem = editingItemId
        ? await MarketingPlanningService.updateItem(editingItemId, payload)
        : await MarketingPlanningService.createItem(activeCampaign.id, payload);
      const itemId = editingItemId || savedItem?.id;
      if (!itemId) throw new Error('No se pudo identificar el contenido guardado');

      const validation = await MarketingPlanningService.validatePublication(itemId);
      if (!validation?.valid) {
        const details = validation?.issues?.map((issue) => issue.message).join(' ') || 'La pieza no cumple los requisitos de Instagram.';
        SnackbarUtils.error(details);
        return;
      }

      const scheduledAt = toScheduledAt(itemForm.plannedDate, itemForm.time);
      await MarketingPlanningService.schedulePublication(itemId, { scheduledAt, provider: 'instagram' });
      setItemModalOpen(false);
      SnackbarUtils.success('Contenido listo y programado para publicar');
      await refreshActiveCampaign();
    } catch (err) {
      console.error('Error scheduling publication:', err);
      SnackbarUtils.error('No se pudo programar la publicación');
    } finally {
      setSchedulingPublication(false);
    }
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs value={topTab} onChange={(event, value) => setTopTab(value)} aria-label="planning tabs">
          <Tab label="Campañas" />
          <Tab label="Previsualización" />
          <Tab label="Configuración" />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {topTab === 2 ? (
          <PlanningSettingsPage
            integrations={integrations}
            loading={integrationsLoading}
            onRefresh={loadIntegrations}
          />
        ) : topTab === 1 ? (
          <FeedPreviewPage campaigns={previewCampaigns} onPreview={setPreviewItem} />
        ) : (
          <>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between">
          <PageHeader
            title="Campañas"
            subtitle="Organizá campañas, calendario editorial y piezas pendientes dentro de Marketing."
          />
          {activeCampaign && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ minWidth: { md: 420 } }}>
              <ProgressSummary label="Avance de campaña" value={timelineStats.percent} detail={timelineStats.detail} />
              <ProgressSummary label="Completitud" value={stats.percent} detail={`${stats.done} de ${stats.total} piezas listas`} />
            </Stack>
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {activeCampaign && (
          <Paper sx={{ p: 3, mt: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControl sx={{ minWidth: 300 }} size="small">
                  <InputLabel>Campaña activa</InputLabel>
                  <Select
                    label="Campaña activa"
                    value={activeCampaign?.id || ''}
                    onChange={(event) => handleSelectCampaign(event.target.value)}
                  >
                    {campaigns.map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id}>{campaign.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(activeCampaign.startDate)} - {formatDate(activeCampaign.endDate)}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" variant="text" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteCampaignOpen(true)}>
                  Eliminar
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setCampaignForm(emptyCampaign()); setCampaignModalOpen(true); }}>
                  Nueva campaña
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {loading ? (
          <Paper sx={{ p: 4, mt: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Paper>
        ) : !activeCampaign ? (
          <Paper sx={{ p: 5, mt: 2, textAlign: 'center', borderRadius: 1 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#e8f1ff',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}
            >
              <CampaignOutlinedIcon />
            </Box>
            <Typography variant="h6" fontWeight={700}>Todavía no hay campañas</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              Creá la primera campaña y definí una rutina para armar el calendario automáticamente.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setCampaignForm(emptyCampaign()); setCampaignModalOpen(true); }}>Nueva campaña</Button>
          </Paper>
        ) : (
          <>
            <Paper sx={{ mb: 2 }}>
              <Tabs value={viewMode} onChange={(event, value) => setViewMode(value)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Mensual" />
                <Tab label="Semanal" />
              </Tabs>

              {viewMode === 0 ? (
                <Box sx={{ p: 3 }}>
                  <PlanningPeriodHeader
                    title={`${monthNames[viewDate.getMonth()]} ${viewDate.getFullYear()}`}
                    subtitle="Calendario mensual"
                    stats={monthStats}
                    onPrev={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
                    onNext={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
                  />
                  <CalendarGrid
                    viewDate={viewDate}
                    selectedDate={selectedDate}
                    items={activeCampaign.items || []}
                    onSelectDate={setSelectedDate}
                  />
                </Box>
              ) : (
                <Box sx={{ p: 3 }}>
                  <WeeklyBoard
                    viewDate={viewDate}
                    items={activeCampaign.items || []}
                    onPrevWeek={() => setViewDate(addDays(viewDate, -7))}
                    onNextWeek={() => setViewDate(addDays(viewDate, 7))}
                    onPreview={setPreviewItem}
                    onEdit={openItemModal}
                    emailTemplateMap={emailTemplateMap}
                    emailListMap={emailListMap}
                    onAdd={(date) => {
                      setSelectedDate(date);
                      setItemForm(emptyItem(date));
                      setEditingItemId(null);
                      setItemModalOpen(true);
                    }}
                  />
                </Box>
              )}
            </Paper>

            {viewMode === 0 && (
            <Paper sx={{ p: 3, overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="h6" fontWeight={700}>{formatLongDate(selectedDate)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedItems.length ? `${dayStats.done} de ${dayStats.total} piezas listas` : 'Sin piezas planificadas para este día'}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  gap: 2,
                  alignItems: 'stretch',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  pb: 1,
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    height: 8
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: '#c6cbd3',
                    borderRadius: 8
                  }
                }}
              >
                {selectedItems.map((item) => (
                  <ContentCard
                    key={item.id}
                    item={item}
                    emailTemplateMap={emailTemplateMap}
                    emailListMap={emailListMap}
                    onPreview={() => setPreviewItem(item)}
                    onEdit={() => openItemModal(item)}
                  />
                ))}
                <AddContentCard onClick={() => openItemModal()} />
              </Box>
            </Paper>
            )}
          </>
        )}
          </>
        )}
      </Box>

      <CampaignDialog
        open={campaignModalOpen}
        form={campaignForm}
        setForm={setCampaignForm}
        onClose={() => setCampaignModalOpen(false)}
        onSave={handleCreateCampaign}
      />

      <ItemDialog
        open={itemModalOpen}
        form={itemForm}
        setForm={setItemForm}
        editing={Boolean(editingItemId)}
        itemId={editingItemId}
        campaignId={activeCampaign?.id}
        onClose={() => setItemModalOpen(false)}
        onSave={handleSaveItem}
        onSchedule={handleSchedulePublication}
        schedulingPublication={schedulingPublication}
        onDelete={handleDeleteItem}
      />

      <InstagramPreviewDialog
        item={previewItem}
        emailTemplateMap={emailTemplateMap}
        emailListMap={emailListMap}
        onClose={() => setPreviewItem(null)}
      />

      <Dialog open={deleteCampaignOpen} onClose={() => setDeleteCampaignOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Eliminar campaña</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Se va a eliminar la campaña "{activeCampaign?.name}" junto con su calendario, piezas planificadas y recursos subidos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCampaignOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDeleteCampaign}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PlanningSettingsPage = ({ integrations, loading, onRefresh }) => {
  const instagram = integrations.find((integration) => integration.provider === 'instagram') || {
    provider: 'instagram',
    status: 'disconnected',
    hasAccessToken: false,
    scopes: []
  };
  const [saving, setSaving] = useState(false);

  const handleConnect = async () => {
    setSaving(true);
    try {
      const redirectUri = `${window.location.origin}${window.location.pathname}`;
      window.localStorage.setItem('axium_meta_redirect_uri', redirectUri);
      const response = await MarketingPlanningService.getIntegrationAuthUrl('instagram', redirectUri);
      window.location.assign(response.authUrl);
    } catch (err) {
      console.error('Error starting Meta OAuth:', err);
      SnackbarUtils.error(getRequestErrorMessage(err, 'No se pudo iniciar la conexión con Meta'));
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setSaving(true);
    try {
      await onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      await MarketingPlanningService.disconnectIntegration('instagram');
      SnackbarUtils.success('Instagram desconectado');
      await onRefresh();
    } catch (err) {
      console.error('Error disconnecting instagram integration:', err);
      SnackbarUtils.error('No se pudo desconectar Instagram');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between">
        <PageHeader
          title="Configuración"
          subtitle="Conectá los canales que el orquestador va a usar para publicar automáticamente."
        />
        <Paper sx={{ px: 2, py: 1.5, minWidth: { md: 280 }, borderRadius: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SettingsOutlinedIcon color="primary" />
            <Box>
              <Typography fontWeight={700}>Canales conectados</Typography>
              <Typography variant="body2" color="text.secondary">
                {integrations.filter((integration) => integration.status === 'connected').length} activos
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      <Paper sx={{ mt: 2, p: 3, borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: instagram.status === 'connected' ? '#e8f5e9' : '#eef2f7',
                  color: instagram.status === 'connected' ? 'success.main' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <LinkOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>Instagram / Meta</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    color={instagram.status === 'connected' ? 'success' : 'default'}
                    label={connectionStatusLabel(instagram.status)}
                  />
                  {instagram.hasAccessToken && <Typography variant="body2" color="text.secondary">Token guardado</Typography>}
                </Stack>
              </Box>
            </Stack>
            <Typography color="text.secondary" sx={{ maxWidth: 620 }}>
              Esta conexión se usa cuando el orquestador automático publica piezas de Instagram desde Meta.
              Axium guarda el token cifrado en backend y no lo muestra en pantalla.
            </Typography>
          </Box>

          <Stack spacing={1} direction="row">
            <Button variant="outlined" onClick={handleRefresh} disabled={loading || saving}>
              Actualizar
            </Button>
            <Button variant="contained" onClick={handleConnect} disabled={saving}>
              {instagram.status === 'connected' ? 'Reconectar con Meta' : 'Conectar con Meta'}
            </Button>
            <Button variant="text" color="error" onClick={handleDisconnect} disabled={saving || instagram.status === 'disconnected'}>
              Desconectar
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2}>
          <ConnectionDetail label="Cuenta" value={instagram.displayName || 'Sin conectar'} />
          <ConnectionDetail label="Meta User ID" value={instagram.metaUserId || '-'} />
          <ConnectionDetail label="Facebook Page ID" value={instagram.facebookPageId || '-'} />
          <ConnectionDetail label="Instagram Business Account ID" value={instagram.instagramBusinessAccountId || '-'} />
          <ConnectionDetail label="Token" value={instagram.hasAccessToken ? 'Guardado en backend' : 'Sin token'} />
          <ConnectionDetail label="Expira" value={instagram.tokenExpiresAt ? formatDateTimeLabel(instagram.tokenExpiresAt) : '-'} />
        </Grid>

        <Alert severity="info" sx={{ mt: 3 }}>
          Al conectar con Meta se solicitan permisos para listar páginas, leer la cuenta de Instagram Business vinculada y publicar contenido.
          Si cambiás permisos o página, usá “Reconectar con Meta”.
        </Alert>
      </Paper>
    </Box>
  );
};

const ConnectionDetail = ({ label, value }) => (
  <Grid item xs={12} md={6}>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography fontWeight={700} sx={{ mt: 0.5, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Paper>
  </Grid>
);

const FeedPreviewPage = ({ campaigns, onPreview }) => {
  const feedItems = useMemo(() => getInstagramFeedPreviewItems(campaigns), [campaigns]);

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between">
        <PageHeader
          title="Previsualización"
          subtitle="Visualizá cómo quedaría el feed de Instagram con las publicaciones planificadas en todas las campañas."
        />
        <Paper variant="outlined" sx={{ p: 1.5, minWidth: { md: 260 }, borderRadius: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <GridOnOutlinedIcon color="primary" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>{feedItems.length} publicaciones con contenido</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>Feed proyectado de Instagram</Typography>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      {!feedItems.length ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: '#e8f1ff',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <PhoneIphoneOutlinedIcon />
          </Box>
          <Typography variant="h6" fontWeight={700}>Todavía no hay publicaciones para previsualizar</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Subí contenido a publicaciones de Instagram para ver cómo quedaría el feed.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems={{ xs: 'center', lg: 'flex-start' }} justifyContent="center">
            <InstagramPhoneFrame items={feedItems} onPreview={onPreview} />
            <Box sx={{ width: { xs: '100%', lg: 320 }, pt: { lg: 2 } }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Publicaciones consideradas</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Se incluyen solo piezas de Instagram con contenido subido, de todas las campañas cargadas, ordenadas por fecha.
              </Typography>
              <Stack spacing={1}>
                {feedItems.slice(0, 6).map((item) => (
                  <Stack key={item.id || `${item.plannedDate}-${item.time}-${item.title}`} direction="row" spacing={1.25} alignItems="center">
                    <Box sx={{ width: 34, height: 34, borderRadius: 1, bgcolor: '#f1f5f9', overflow: 'hidden', flex: '0 0 auto' }}>
                      {getPrimaryAsset(item)?.previewUrl && (
                        <Box component="img" src={getPrimaryAsset(item).previewUrl} alt={item.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>{item.title || item.contentType}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{formatDate(item.plannedDate)} · {item.contentType}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

const InstagramPhoneFrame = ({ items, onPreview }) => (
  <Box
    sx={{
      width: 360,
      height: 720,
      maxWidth: '100%',
      boxSizing: 'border-box',
      borderRadius: 5,
      border: '10px solid #111827',
      bgcolor: '#e5e7eb',
      boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22)',
      overflow: 'hidden'
    }}
  >
    <Box sx={{ height: 26, bgcolor: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ width: 72, height: 5, borderRadius: 5, bgcolor: '#374151' }} />
    </Box>
    <Box
      sx={{
        height: 'calc(100% - 26px)',
        bgcolor: '#e5e7eb',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#c6cbd3', borderRadius: 6 }
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: '#d1d5db', bgcolor: '#e5e7eb' }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#111827', flex: '0 0 auto' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800}>Instagram</Typography>
            <Typography variant="caption" color="text.secondary">{items.length} publicaciones planificadas</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={2.25} sx={{ mt: 1.5 }}>
          <ProfileMetric value={items.length} label="posts" />
          <ProfileMetric value={items.filter((item) => item.contentType === 'Reel').length} label="reels" />
          <ProfileMetric value={items.filter((item) => item.contentType === 'Carrousel').length} label="carrousel" />
        </Stack>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, bgcolor: '#e5e7eb', p: 0.5, minHeight: 500, alignContent: 'start' }}>
        {items.map((item) => {
          const asset = getPrimaryAsset(item);
          return (
            <Box
              key={item.id || `${item.plannedDate}-${item.time}-${item.title}`}
              role="button"
              tabIndex={0}
              onClick={() => onPreview(item)}
              sx={{
                width: '100%',
                height: 0,
                pt: '125%',
                bgcolor: '#f8fafc',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer'
              }}
            >
              <Box
                component="img"
                src={asset.previewUrl}
                alt={item.title}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <Box sx={{ position: 'absolute', left: 5, bottom: 5 }}>
                <Chip size="small" label={item.contentType} sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(17, 24, 39, 0.72)', color: '#fff' }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  </Box>
);

const ProfileMetric = ({ value, label }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2" fontWeight={800}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

const CalendarGrid = ({ viewDate, selectedDate, items, onSelectDate }) => {
  const first = startOfMonth(viewDate);
  const offset = (first.getDay() + 6) % 7;
  const start = addDays(first, -offset);
  const days = Array.from({ length: 42 }, (_, index) => addDays(start, index));

  return (
    <Box>
      <Grid container columns={7} sx={{ mb: 1 }}>
        {weekdays.map((day) => (
          <Grid item xs={1} key={day.value}>
            <Typography align="center" variant="body2" color="text.secondary" fontWeight={700}>{day.label}</Typography>
          </Grid>
        ))}
      </Grid>
      <Grid container columns={7} sx={{ borderTop: 1, borderLeft: 1, borderColor: 'divider' }}>
        {days.map((day) => {
          const iso = toDateInput(day);
          const dayItems = items.filter((item) => item.plannedDate === iso);
          const hasPending = dayItems.some(isMissingContent);
          const isOutside = day.getMonth() !== viewDate.getMonth();
          const isSelected = selectedDate === iso;
          return (
            <Grid item xs={1} key={iso}>
              <Box
                onClick={() => onSelectDate(iso)}
                sx={{
                  minHeight: 118,
                  p: 1,
                  borderRight: 1,
                  borderBottom: 1,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  boxShadow: isSelected ? 'inset 0 0 0 1px' : 'none',
                  color: isOutside ? 'text.disabled' : 'text.primary',
                  bgcolor: isSelected ? 'primary.50' : 'background.paper',
                  cursor: 'pointer',
                  overflow: 'hidden'
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography fontWeight={700}>{day.getDate()}</Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {hasPending && (
                      <Box
                        title="Hay contenido pendiente"
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#dc2626'
                        }}
                      />
                    )}
                    {dayItems.length > 0 && <Typography variant="caption">{dayItems.length}</Typography>}
                  </Stack>
                </Stack>
                <Stack spacing={0.5} sx={{ mt: 1, minWidth: 0 }}>
                  {dayItems.slice(0, 3).map((item) => (
                    <ContentTypeBadge key={item.id} item={item} compact />
                  ))}
                  {dayItems.length > 3 && <Typography variant="caption" color="text.secondary">+{dayItems.length - 3} más</Typography>}
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const PlanningPeriodHeader = ({ title, subtitle, stats, onPrev, onNext }) => (
  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <RoundNavButton direction="prev" onClick={onPrev} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }} noWrap>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <RoundNavButton direction="next" onClick={onNext} />
      </Stack>
      <Stack spacing={0.75} sx={{ width: { xs: '100%', md: 260 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary" fontWeight={700}>
            Contenido listo
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {stats.done} de {stats.total}
          </Typography>
        </Stack>
        <LinearProgress variant="determinate" value={stats.percent} sx={{ height: 7, borderRadius: 7 }} />
      </Stack>
    </Stack>
  </Paper>
);

const RoundNavButton = ({ direction, onClick }) => (
  <IconButton
    onClick={onClick}
    sx={{
      width: 44,
      height: 44,
      p: 0,
      borderRadius: '50%',
      bgcolor: '#f5f5f5',
      flex: '0 0 44px',
      '&:hover': { bgcolor: '#eeeeee' }
    }}
  >
    {direction === 'prev' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
  </IconButton>
);

const WeeklyBoard = ({ viewDate, items, emailTemplateMap, emailListMap, onPrevWeek, onNextWeek, onPreview, onEdit, onAdd }) => {
  const start = startOfWeek(viewDate);
  const days = Array.from({ length: 7 }, (_, index) => addDays(start, index));
  const weekItemDates = new Set(days.map((day) => toDateInput(day)));
  const weekItems = items.filter((item) => weekItemDates.has(item.plannedDate));
  const weekStats = getStats(weekItems);

  return (
    <Box>
      <PlanningPeriodHeader
        title={`${formatDate(toDateInput(days[0]))} - ${formatDate(toDateInput(days[6]))}`}
        subtitle="Semana editorial"
        stats={weekStats}
        onPrev={onPrevWeek}
        onNext={onNextWeek}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 260px)',
          gap: 1.5,
          overflowX: 'auto',
          pb: 1
        }}
      >
        {days.map((day) => {
          const iso = toDateInput(day);
          const dayItems = items.filter((item) => item.plannedDate === iso).sort(sortByTime);
          return (
            <Paper
              key={iso}
              variant="outlined"
              sx={{
                p: 1.25,
                minHeight: 520,
                bgcolor: '#fafafa',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ mb: 1.25 }}>
                <Typography variant="subtitle2" fontWeight={700}>{weekdays.find((weekday) => weekday.value === day.getDay())?.label}</Typography>
                <Typography variant="body2" color="text.secondary">{formatLongDate(iso)}</Typography>
              </Box>
              <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {dayItems.map((item) => (
                  <WeeklyContentCard
                    key={item.id}
                    item={item}
                    emailTemplateMap={emailTemplateMap}
                    emailListMap={emailListMap}
                    onPreview={() => onPreview(item)}
                    onEdit={() => onEdit(item)}
                  />
                ))}
                <WeeklyAddCard onClick={() => onAdd(iso)} />
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

const WeeklyAddCard = ({ onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: '100%',
      minHeight: 88,
      border: '1px dashed',
      borderColor: 'primary.light',
      borderRadius: 1,
      bgcolor: '#f8fbff',
      color: 'primary.main',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,
      font: 'inherit',
      transition: 'background-color 0.2s, border-color 0.2s',
      '&:hover': {
        bgcolor: '#f1f7ff',
        borderColor: 'primary.main'
      }
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: '50%',
        border: '1px solid',
        borderColor: 'primary.light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <AddIcon fontSize="small" />
    </Box>
    <Typography variant="body2" fontWeight={700}>Agregar publicación</Typography>
  </Box>
);

const WeeklyContentCard = ({ item, emailTemplateMap, emailListMap, onPreview, onEdit }) => (
  <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, bgcolor: 'background.paper', minWidth: 0, overflow: 'hidden' }}>
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
      <Typography variant="body2" fontWeight={700}>{item.time || 'Sin hora'}</Typography>
      <Stack direction="row" spacing={0.25}>
        <IconButton size="small" onClick={onPreview} disabled={!canPreviewItem(item)} sx={{ width: 30, height: 30 }}>
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onEdit} sx={{ width: 30, height: 30 }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
    {item.channel === 'Email Marketing' ? (
      <Chip size="small" label="Email" sx={{ bgcolor: '#ffedd5', color: '#9a3412', fontWeight: 700, width: 'fit-content' }} />
    ) : (
      <ContentTypeBadge item={item} compact />
    )}
    <Typography variant="body2" fontWeight={700} sx={{ mt: 1, minWidth: 0 }} noWrap>
      {item.channel === 'Email Marketing' ? emailDisplayTitle(item, emailTemplateMap) : item.title}
    </Typography>
    <Typography variant="caption" color="text.secondary" noWrap>
      {item.channel === 'Email Marketing' ? emailCardDescription(item, emailTemplateMap, emailListMap) : (item.objective || item.copy || 'Pendiente de completar')}
    </Typography>
    {isMissingContent(item) && (
      <Chip
        size="small"
        icon={<WarningAmberRoundedIcon />}
        label="Pendiente"
        sx={{ mt: 1, bgcolor: '#fee2e2', color: '#b91c1c', '& .MuiChip-icon': { color: '#b91c1c' } }}
      />
    )}
  </Paper>
);

const ProgressSummary = ({ label, value, detail }) => (
  <Paper variant="outlined" sx={{ p: 1.25, flex: 1, minWidth: 190, bgcolor: 'background.paper' }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography>
      <Typography variant="body2" fontWeight={700}>{value}%</Typography>
    </Stack>
    <LinearProgress variant="determinate" value={value} sx={{ height: 6, borderRadius: 6, mb: 0.75 }} />
    <Typography variant="caption" color="text.secondary" noWrap>{detail}</Typography>
  </Paper>
);

const ContentTypeBadge = ({ item, compact = false }) => {
  const style = contentTypeStyles[item.contentType] || { bg: '#f1f5f9', color: '#334155', border: '#cbd5e1' };
  const missing = isMissingContent(item);

  return (
    <Box
      sx={{
        minWidth: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.5,
        height: compact ? 22 : 26,
        pl: compact ? 0.75 : 1,
        pr: compact ? 0.45 : 1,
        borderRadius: 999,
        bgcolor: style.bg,
        color: style.color,
        border: '1px solid',
        borderColor: style.border,
        fontSize: compact ? 12 : 13,
        fontWeight: 700,
        lineHeight: 1,
        width: compact ? 'calc(100% - 8px)' : '100%',
        maxWidth: '100%'
      }}
    >
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.contentType}
      </Box>
      <Box
        component="span"
        title={missing ? 'Falta contenido' : 'Contenido cargado'}
        sx={{
          width: compact ? 16 : 18,
          height: compact ? 16 : 18,
          borderRadius: '50%',
          bgcolor: missing ? '#fee2e2' : '#dcfce7',
          color: missing ? '#b91c1c' : '#15803d',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto'
        }}
      >
        {missing ? (
          <WarningAmberRoundedIcon sx={{ fontSize: compact ? 12 : 14 }} />
        ) : (
          <CheckCircleRoundedIcon sx={{ fontSize: compact ? 12 : 14 }} />
        )}
      </Box>
    </Box>
  );
};

const ContentRow = ({ item, onEdit }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography fontWeight={700}>{item.title}</Typography>
          <Chip size="small" label={item.channel} />
          <Chip size="small" label={item.contentType} />
          <Chip size="small" color={statusColor(getCalculatedStatus(item))} label={getCalculatedStatus(item)} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {formatDate(item.plannedDate)} {item.time || ''} · {item.objective || item.copy || item.notes || 'Pendiente de completar'}
        </Typography>
      </Box>
      <IconButton onClick={onEdit} aria-label="Editar contenido">
        <EditIcon />
      </IconButton>
    </Stack>
  </Paper>
);

const InstagramContentPreview = ({ item }) => (
  <Box
    sx={{
      position: 'relative',
      height: 132,
      borderRadius: 1,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: '#f6f7f9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 1.25
    }}
  >
    {getPrimaryAsset(item)?.previewUrl ? (
      getPrimaryAsset(item)?.mimeType?.startsWith('video/') ? (
        <Box
          component="video"
          src={getPrimaryAsset(item).previewUrl}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          muted
          playsInline
        />
      ) : getPrimaryAsset(item)?.mimeType === 'application/pdf' ? (
        <Typography variant="body2" color="text.secondary">
          PDF cargado
        </Typography>
      ) : (
        <Box
          component="img"
          src={getPrimaryAsset(item).previewUrl}
          alt={item.title}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )
    ) : (
      <Typography variant="body2" color="text.secondary">
        Sin contenido
      </Typography>
    )}
    <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
      <ContentTypeBadge item={item} compact />
    </Box>
  </Box>
);

const EmailContentPreview = ({ item, emailTemplateMap, emailListMap }) => {
  const hasTemplate = Boolean(item.emailTemplateId);
  const hasList = Boolean(item.emailListId);
  const templateName = emailTemplateName(item, emailTemplateMap);
  const listName = emailListName(item, emailListMap);

  return (
    <Box
      sx={{
        height: 132,
        borderRadius: 1,
        border: '1px solid',
        borderColor: hasTemplate && hasList ? '#bfdbfe' : '#fecaca',
        bgcolor: hasTemplate && hasList ? '#eff6ff' : '#fef2f2',
        mb: 1.25,
        p: 1.25,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0 }}>
          Email
        </Typography>
        <Box
          component="span"
          title={hasTemplate && hasList ? 'Listo para programar' : 'Falta plantilla o lista'}
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: hasTemplate && hasList ? '#dcfce7' : '#fee2e2',
            color: hasTemplate && hasList ? '#15803d' : '#b91c1c',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto'
          }}
        >
          {hasTemplate && hasList ? <CheckCircleRoundedIcon sx={{ fontSize: 15 }} /> : <WarningAmberRoundedIcon sx={{ fontSize: 15 }} />}
        </Box>
      </Stack>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {hasTemplate ? templateName : 'Falta plantilla'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {hasList ? `Lista: ${listName}` : 'Falta lista'}
        </Typography>
      </Box>
    </Box>
  );
};

const ContentCard = ({ item, emailTemplateMap, emailListMap, onPreview, onEdit }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 1,
      width: 260,
      flex: '0 0 260px',
      minHeight: 276,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: 'none'
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={700} sx={{ flex: '0 0 auto' }}>{item.time || 'Sin hora'}</Typography>
        <Chip
          size="small"
          label={item.channel}
          sx={{
            minWidth: 0,
            maxWidth: 118,
            '& .MuiChip-label': {
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }
          }}
        />
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <IconButton
          size="small"
          onClick={onPreview}
          disabled={!canPreviewItem(item)}
          aria-label={item.channel === 'Email Marketing' ? 'Previsualizar email' : 'Previsualizar en Instagram'}
          sx={{ width: 36, height: 36, flex: '0 0 36px' }}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onEdit}
          aria-label="Editar contenido"
          sx={{ width: 36, height: 36, flex: '0 0 36px' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>

    {item.channel === 'Email Marketing' ? (
      <EmailContentPreview item={item} emailTemplateMap={emailTemplateMap} emailListMap={emailListMap} />
    ) : (
      <InstagramContentPreview item={item} />
    )}

    {item.channel !== 'Email Marketing' && (
      <>
        <Typography fontWeight={700} sx={{ mb: 0.5, minWidth: 0 }} noWrap>
          {item.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 22, minWidth: 0, maxWidth: '100%' }} noWrap>
          {item.objective || item.copy || item.notes || 'Pendiente de completar'}
        </Typography>
      </>
    )}
    <Box sx={{ mt: 'auto', pt: 1.25 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
        {getCalculatedStatus(item) !== 'Pendiente' && (
          <Chip size="small" color={statusColor(getCalculatedStatus(item))} label={getCalculatedStatus(item)} />
        )}
        {isMissingContent(item) && (
          <Chip
            size="small"
            icon={<WarningAmberRoundedIcon />}
            label="Falta contenido"
            sx={{
              bgcolor: '#fee2e2',
              color: '#b91c1c',
              '& .MuiChip-icon': { color: '#b91c1c' }
            }}
          />
        )}
      </Stack>
    </Box>
  </Paper>
);

const AddContentCard = ({ onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: 260,
      flex: '0 0 260px',
      minHeight: 276,
      border: '1px dashed',
      borderColor: 'primary.light',
      borderRadius: 1,
      bgcolor: 'background.paper',
      color: 'primary.main',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      transition: 'background-color 0.2s, border-color 0.2s',
      '&:hover': {
        bgcolor: '#f5f9ff',
        borderColor: 'primary.main'
      }
    }}
  >
    <Box
      sx={{
        width: 54,
        height: 54,
        borderRadius: '50%',
        border: '1px solid',
        borderColor: 'primary.light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <AddIcon />
    </Box>
    <Typography fontWeight={700}>Agregar publicación</Typography>
  </Box>
);

const InstagramPreviewDialog = ({ item, emailTemplateMap, emailListMap, onClose }) => {
  if (!item) return null;
  if (item.channel === 'Email Marketing') {
    return <EmailMarketingPreviewDialog item={item} emailTemplateMap={emailTemplateMap} emailListMap={emailListMap} onClose={onClose} />;
  }
  const aspectValue = item.assetAspectRatio || '1:1';
  const aspect = aspectValue.replace(':', ' / ');
  const previewMaxWidth = aspectValue === '9:16' ? 190 : aspectValue === '1.91:1' ? 320 : 240;
  const primaryAsset = getPrimaryAsset(item);
  const coverAsset = getCoverAsset(item);
  const carouselAssets = getCarouselAssets(item);
  const previewAssets = item.contentType === 'Carrousel' ? carouselAssets : [primaryAsset].filter(Boolean);
  const calculatedStatus = getCalculatedStatus(item);

  return (
    <Dialog
      open={Boolean(item)}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { borderRadius: 1, width: 600, maxWidth: 'calc(100vw - 64px)' } }}
    >
      <DialogTitle sx={{ px: 3, py: 2 }}>
        <Typography variant="h6" fontWeight={700}>Preview Instagram</Typography>
        <Typography variant="body2" color="text.secondary">Revisá publicación, horario y contenido antes de programar.</Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#fafafa', p: 2, overflowY: 'auto' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          sx={{ width: 'fit-content', maxWidth: '100%', mx: 'auto' }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: previewMaxWidth,
              maxWidth: previewMaxWidth,
              mx: { xs: 'auto', md: 0 },
              overflow: 'hidden',
              borderRadius: 1,
              bgcolor: 'background.paper',
              flex: '0 0 auto'
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.25, py: 1 }}>
              <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: '#111827' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700}>Instagram</Typography>
                <Typography variant="caption" color="text.secondary">{item.contentType} · {aspectValue}</Typography>
              </Box>
            </Stack>
            {item.contentType === 'Carrousel' ? (
              <Box sx={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory' }}>
                {previewAssets.map((asset, index) => (
                  <Box key={asset.s3Key} sx={{ width: '100%', flex: '0 0 100%', aspectRatio: aspect, scrollSnapAlign: 'start', bgcolor: '#f3f4f6' }}>
                    <Box component="img" src={asset.previewUrl} alt={`${item.title} ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: aspect,
                  bgcolor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                {primaryAsset?.mimeType?.startsWith('video/') ? (
                  <Box component="video" src={primaryAsset.previewUrl} poster={coverAsset?.previewUrl} controls sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Box component="img" src={primaryAsset?.previewUrl || coverAsset?.previewUrl} alt={item.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </Box>
            )}
            <Box sx={{ px: 1.25, py: 1.25 }}>
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', display: 'block' }}>
                <Box component="span" fontWeight={700}>Instagram </Box>
                {item.copy || 'Sin copy cargado.'}
              </Typography>
            </Box>
          </Paper>

          <Stack spacing={1.25} sx={{ width: 280, minWidth: 0, pt: 0.5 }}>
            <Stack spacing={1.25}>
              <PreviewInfoRow label="Fecha" value={publicationScheduleLabel(item)} />
              <PreviewInfoRow label="Canal" value={item.channel} />
              <PreviewInfoRow label="Tipo" value={item.contentType} />
              <PreviewInfoRow label="Formato" value={aspectValue} />
              {item.title && <PreviewInfoRow label="Título" value={item.title} />}
            </Stack>
            <Divider />
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ pt: 0.25 }}>
              <Chip size="small" color={statusColor(calculatedStatus)} label={calculatedStatus} />
              {isMissingContent(item) && (
                <Chip
                  size="small"
                  icon={<WarningAmberRoundedIcon />}
                  label="Pendiente"
                  sx={{ bgcolor: '#fee2e2', color: '#b91c1c', '& .MuiChip-icon': { color: '#b91c1c' } }}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

const EmailMarketingPreviewDialog = ({ item, emailTemplateMap, emailListMap, onClose }) => {
  const [template, setTemplate] = useState(emailTemplateMap?.get(String(item.emailTemplateId)) || null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const list = emailListMap?.get(String(item.emailListId));

  useEffect(() => {
    let mounted = true;
    const loadTemplate = async () => {
      const fallbackTemplate = emailTemplateMap?.get(String(item.emailTemplateId)) || null;
      setTemplate(fallbackTemplate);
      if (!item.emailTemplateId) return;
      setLoadingTemplate(true);
      try {
        const fullTemplate = await EmailTemplateService.getTemplateById(item.emailTemplateId);
        if (mounted) setTemplate(unwrapEmailTemplate(fullTemplate) || fallbackTemplate);
      } catch (err) {
        console.error('Error loading email template preview:', err);
        if (mounted) setTemplate(fallbackTemplate);
      } finally {
        if (mounted) setLoadingTemplate(false);
      }
    };

    loadTemplate();
    return () => {
      mounted = false;
    };
  }, [item.emailTemplateId, emailTemplateMap]);

  return (
    <Dialog open={Boolean(item)} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 1 } }}>
      <DialogTitle sx={{ px: 3, py: 2 }}>
        <Typography variant="h6" fontWeight={700}>Preview Email Marketing</Typography>
        <Typography variant="body2" color="text.secondary">Así se verá el correo que se programaría para la lista seleccionada.</Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#fafafa', p: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Paper variant="outlined" sx={{ width: { xs: '100%', md: 260 }, p: 2, borderRadius: 1, bgcolor: 'background.paper', flex: '0 0 auto' }}>
            <Stack spacing={1.25}>
              <PreviewInfoRow label="Fecha" value={publicationScheduleLabel(item)} />
              <PreviewInfoRow label="Plantilla" value={template?.name || 'Sin plantilla'} />
              <PreviewInfoRow label="Lista" value={list?.name || 'Sin lista'} />
              <PreviewInfoRow label="Asunto" value={template?.subject || item.title || 'Sin asunto'} />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ flex: 1, width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {template?.subject || 'Sin asunto'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {template?.name || 'Plantilla de email'}
              </Typography>
            </Box>
            <Box sx={{ minHeight: 360, maxHeight: 520, overflow: 'auto', bgcolor: '#fff' }}>
              {loadingTemplate ? (
                <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : template?.htmlContent ? (
                <Box sx={{ p: 2 }} dangerouslySetInnerHTML={{ __html: template.htmlContent }} />
              ) : (
                <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No hay plantilla para previsualizar.</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

const PreviewInfoRow = ({ label, value }) => (
  <Stack direction="row" spacing={2} alignItems="baseline" justifyContent="space-between">
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0, fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} textAlign="right" sx={{ minWidth: 0, maxWidth: 170 }}>
      {value}
    </Typography>
  </Stack>
);

const CampaignDialog = ({ open, form, setForm, onClose, onSave }) => {
  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateRule = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, ruleIndex) => {
        if (ruleIndex !== index) return rule;
        if (field === 'channel') {
          return {
            ...rule,
            channel: value,
            contentType: value === 'Email Marketing'
              ? 'Email'
              : (instagramContentTypes.includes(rule.contentType) ? rule.contentType : 'Post')
          };
        }
        return { ...rule, [field]: value };
      })
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 1, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ px: 3, py: 2.25 }}>
        <Typography variant="h6" fontWeight={700}>Nueva campaña</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Definí el objetivo y una rutina para precargar el calendario.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3, bgcolor: '#fafafa' }}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Información general</Typography>
          <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField size="small" fullWidth label="Nombre" value={form.name} onChange={(event) => updateField('name', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField size="small" fullWidth type="date" label="Inicio" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(event) => updateField('startDate', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField size="small" fullWidth type="date" label="Fin" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(event) => updateField('endDate', event.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField size="small" fullWidth label="Objetivo" value={form.objective} onChange={(event) => updateField('objective', event.target.value)} />
          </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'background.paper' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Rutina de contenido</Typography>
            <Typography variant="body2" color="text.secondary">Esto genera el esqueleto inicial. Después el calendario queda libre.</Typography>
            </Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => updateField('rules', [...form.rules, { weekdays: [1], channel: 'Instagram', contentType: 'Historia', time: '09:00' }])}
            >
              Agregar regla
            </Button>
          </Stack>

          <Stack spacing={1.5}>
          {form.rules.map((rule, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 1.5, bgcolor: '#fbfbfb' }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={3.6}>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {weekdays.map((day) => (
                      <Chip
                        key={day.value}
                        clickable
                        size="small"
                        color={rule.weekdays.includes(day.value) ? 'primary' : 'default'}
                        label={day.label}
                        onClick={() => {
                          const next = rule.weekdays.includes(day.value)
                            ? rule.weekdays.filter((value) => value !== day.value)
                            : [...rule.weekdays, day.value];
                          updateRule(index, 'weekdays', next);
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={rule.channel === 'Email Marketing' ? 3.4 : 2.55}>
                  <TextField size="small" select fullWidth label="Canal" value={rule.channel} onChange={(event) => updateRule(index, 'channel', event.target.value)}>
                    {channels.map((channel) => <MenuItem key={channel} value={channel}>{channel}</MenuItem>)}
                  </TextField>
                </Grid>
                {rule.channel === 'Instagram' && (
                  <Grid item xs={12} md={2.55}>
                    <TextField size="small" select fullWidth label="Tipo" value={rule.contentType} onChange={(event) => updateRule(index, 'contentType', event.target.value)}>
                      {instagramContentTypes.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={10} md={2.8}>
                  <TextField
                    size="small"
                    fullWidth
                    type="time"
                    label="Hora"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    value={rule.time || ''}
                    onChange={(event) => updateRule(index, 'time', event.target.value)}
                    sx={{ minWidth: 170 }}
                  />
                </Grid>
                <Grid item xs={2} md={0.5}>
                  <IconButton size="small" onClick={() => updateField('rules', form.rules.filter((_, ruleIndex) => ruleIndex !== index))}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
          </Stack>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper' }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={onSave} disabled={!form.name || !form.objective || !form.startDate || !form.endDate}>Crear campaña</Button>
      </DialogActions>
    </Dialog>
  );
};

const ItemDialog = ({ open, form, setForm, editing, itemId, campaignId, onClose, onSave, onSchedule, onDelete, schedulingPublication }) => {
  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [pendingCropMeta, setPendingCropMeta] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailLists, setEmailLists] = useState([]);
  const [emailOptionsLoading, setEmailOptionsLoading] = useState(false);
  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateEmailTemplate = (templateId) => {
    const selectedTemplate = emailTemplates.find((template) => String(template.id) === String(templateId));
    setForm((prev) => ({
      ...prev,
      emailTemplateId: templateId,
      title: !prev.title || prev.title === 'Email marketing' ? (selectedTemplate?.name || prev.title) : prev.title
    }));
  };
  const updateInstagramContentType = (value) => {
    const nextAspectOptions = getInstagramAspectOptions(value);
    setForm((prev) => ({
      ...prev,
      contentType: value,
      assetAspectRatio: nextAspectOptions.some((option) => option.value === prev.assetAspectRatio)
        ? prev.assetAspectRatio
        : (nextAspectOptions[0]?.value || '1:1')
    }));
  };
  const updateChannel = (value) => {
    const nextType = value === 'Email Marketing'
      ? 'Email'
      : (instagramContentTypes.includes(form.contentType) ? form.contentType : 'Historia');
    const nextAspectOptions = getInstagramAspectOptions(nextType);
    setForm((prev) => ({
      ...prev,
      channel: value,
      contentType: nextType,
      assetAspectRatio: value === 'Instagram' && !nextAspectOptions.some((option) => option.value === prev.assetAspectRatio)
        ? (nextAspectOptions[0]?.value || '1:1')
        : prev.assetAspectRatio,
      emailTemplateId: value === 'Email Marketing' ? prev.emailTemplateId : '',
      emailListId: value === 'Email Marketing' ? prev.emailListId : ''
    }));
  };
  const aspectOptions = useMemo(() => getInstagramAspectOptions(form.contentType), [form.contentType]);
  const selectedAspect = aspectOptions.find((option) => option.value === form.assetAspectRatio) || aspectOptions[0] || instagramAspectRatios[0];
  const canSaveContent = form.channel === 'Email Marketing'
    ? Boolean(form.emailTemplateId && form.emailListId)
    : Boolean(form.title && form.plannedDate);

  useEffect(() => {
    if (!open || form.channel !== 'Instagram' || !aspectOptions.length) return;
    if (aspectOptions.some((option) => option.value === form.assetAspectRatio)) return;
    setForm((prev) => ({ ...prev, assetAspectRatio: aspectOptions[0].value }));
  }, [aspectOptions, form.assetAspectRatio, form.channel, open, setForm]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setUploading(false);
      setImageSrc('');
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setPendingCropMeta(null);
    }
  }, [open, editing]);

  useEffect(() => {
    let mounted = true;
    if (!open || form.channel !== 'Email Marketing') return undefined;

    const loadEmailOptions = async () => {
      setEmailOptionsLoading(true);
      try {
        const [templatesResponse, listsResponse] = await Promise.all([
          EmailTemplateService.getTemplates(1, 200, ''),
          EmailListService.getLists(1, 200, '')
        ]);
        if (!mounted) return;
        const baseLists = extractEmailLists(listsResponse);
        const listsWithCounts = await enrichEmailListsWithCounts(baseLists);
        setEmailTemplates(extractEmailTemplates(templatesResponse));
        setEmailLists(listsWithCounts);
      } catch (err) {
        console.error('Error loading email marketing options:', err);
        SnackbarUtils.error('No se pudieron cargar plantillas o listas');
      } finally {
        if (mounted) setEmailOptionsLoading(false);
      }
    };

    loadEmailOptions();
    return () => {
      mounted = false;
    };
  }, [open, form.channel]);

  const handleUploadAsset = async (event, role = 'primary', sortOrder = 0) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length || !campaignId) return;

    const file = files[0];
    if (form.channel === 'Instagram' && role !== 'carousel_slide' && file.type.startsWith('image/')) {
      setImageSrc(await readFile(file));
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setPendingCropMeta({ role, sortOrder });
      return;
    }

    for (let index = 0; index < files.length; index += 1) {
      if (form.contentType === 'Reel' && role === 'primary' && files[index].type.startsWith('video/')) {
        const metadata = await getVideoMetadata(files[index]);
        if (metadata.duration < 3 || metadata.duration > 90) {
          SnackbarUtils.error('El reel debe durar entre 3 y 90 segundos.');
          continue;
        }
        if (metadata.width < 540 || metadata.height < 960) {
          SnackbarUtils.error('El reel debe tener al menos 540x960 px.');
          continue;
        }
      }
      await uploadAssetFile(files[index], files[index].type || '', role, sortOrder + index);
    }
  };

  const uploadAssetFile = async (file, mimeType, role = 'primary', sortOrder = 0) => {
    if (!campaignId) return;

    setUploading(true);
    try {
      const uploaded = await MarketingPlanningService.uploadAsset(campaignId, file, {
        role,
        itemId,
        aspectRatio: form.assetAspectRatio || selectedAspect.value,
        sortOrder
      });
      const nextAsset = uploadResponseToAsset(uploaded, file);
      setForm((prev) => ({
        ...prev,
        assets: upsertAsset(prev.assets || [], nextAsset),
        assetKey: role === 'primary' ? uploaded.assetKey || '' : prev.assetKey,
        assetAspectRatio: prev.assetAspectRatio || selectedAspect.value,
        assetPreviewUrl: role === 'primary' ? uploaded.previewUrl || '' : prev.assetPreviewUrl,
        assetMimeType: role === 'primary' ? uploaded.mimeType || mimeType || file.type || '' : prev.assetMimeType
      }));
      setImageSrc('');
      setPendingCropMeta(null);
      SnackbarUtils.success('Recurso subido');
    } catch (err) {
      console.error('Error uploading marketing planning asset:', err);
      SnackbarUtils.error('No se pudo subir el recurso');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadCroppedImage = async () => {
    if (!imageSrc || !croppedAreaPixels || !pendingCropMeta) return;
    try {
      const { file } = await getCroppedImg(imageSrc, croppedAreaPixels);
      await uploadAssetFile(file, file.type, pendingCropMeta.role, pendingCropMeta.sortOrder);
    } catch (err) {
      console.error('Error cropping marketing planning image:', err);
      SnackbarUtils.error('No se pudo recortar la imagen');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 1, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ px: 3, py: 2.25 }}>
        <Typography variant="h6" fontWeight={700}>{editing ? 'Editar contenido' : 'Nuevo contenido'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Cargá la pieza o dejala preparada para completar después.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3, bgcolor: '#fafafa' }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            label="1. Configuración"
            color={step === 0 ? 'primary' : 'default'}
            variant={step === 0 ? 'filled' : 'outlined'}
          />
          <Chip
            label="2. Contenido"
            color={step === 1 ? 'primary' : 'default'}
            variant={step === 1 ? 'filled' : 'outlined'}
          />
        </Stack>
        <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'background.paper' }}>
          {step === 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField size="small" fullWidth type="date" label="Fecha" InputLabelProps={{ shrink: true }} value={form.plannedDate} onChange={(event) => updateField('plannedDate', event.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField size="small" fullWidth type="time" label="Hora" InputLabelProps={{ shrink: true }} value={form.time || ''} onChange={(event) => updateField('time', event.target.value)} />
              </Grid>
              <Grid item xs={12} md={form.channel === 'Instagram' ? 6 : 12}>
                <TextField size="small" select fullWidth label="Canal" value={form.channel} onChange={(event) => updateChannel(event.target.value)}>
                  {channels.map((channel) => <MenuItem key={channel} value={channel}>{channel}</MenuItem>)}
                </TextField>
              </Grid>
              {form.channel === 'Instagram' && (
                <Grid item xs={12} md={6}>
                  <TextField size="small" select fullWidth label="Tipo" value={form.contentType} onChange={(event) => updateInstagramContentType(event.target.value)}>
                    {instagramContentTypes.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Título" value={form.title} onChange={(event) => updateField('title', event.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  label="Objetivo del contenido"
                  value={form.objective || ''}
                  onChange={(event) => updateField('objective', event.target.value)}
                />
              </Grid>
            </Grid>
          ) : form.channel === 'Email Marketing' ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {contentGuidance(form.contentType, form.channel)}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  label="Plantilla"
                  value={form.emailTemplateId || ''}
                  onChange={(event) => updateEmailTemplate(event.target.value)}
                  disabled={emailOptionsLoading}
                >
                  {emailTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name} {template.subject ? `· ${template.subject}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  label="Lista"
                  value={form.emailListId || ''}
                  onChange={(event) => updateField('emailListId', event.target.value)}
                  disabled={emailOptionsLoading}
                >
                  {emailLists.map((list) => (
                    <MenuItem key={list.id} value={list.id}>
                      {list.name}{typeof list.contactsCount === 'number' ? ` · ${list.contactsCount} contactos` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth multiline minRows={2} label="Notas internas" value={form.notes || ''} onChange={(event) => updateField('notes', event.target.value)} />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Formato Instagram</Typography>
                  <Tooltip title={instagramFormatHelp(form.contentType)}>
                    <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Tooltip>
                </Stack>
                {aspectOptions.length === 1 ? (
                  <Chip
                    color="primary"
                    label={`${aspectOptions[0].label} · ${aspectOptions[0].description}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {aspectOptions.map((option) => (
                      <Chip
                        key={option.value}
                        clickable
                        color={form.assetAspectRatio === option.value ? 'primary' : 'default'}
                        variant={form.assetAspectRatio === option.value ? 'filled' : 'outlined'}
                        label={`${option.label} · ${option.description}`}
                        onClick={() => updateField('assetAspectRatio', option.value)}
                      />
                    ))}
                  </Stack>
                )}
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {contentGuidance(form.contentType, form.channel)}
                </Alert>
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: form.assetKey ? 'primary.light' : 'divider',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: '#fbfbfb'
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700}>Recursos de la pieza</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {assetSummary(form)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                      {form.contentType === 'Reel' ? (
                        <>
                          <Button component="label" variant="outlined" startIcon={uploading ? <CircularProgress size={16} /> : <FileUploadOutlinedIcon />} disabled={uploading || !campaignId}>
                            Video
                            <input hidden type="file" accept="video/*" onChange={(event) => handleUploadAsset(event, 'primary', 0)} />
                          </Button>
                          <Button component="label" variant="outlined" startIcon={<FileUploadOutlinedIcon />} disabled={uploading || !campaignId}>
                            Portada
                            <input hidden type="file" accept="image/*" onChange={(event) => handleUploadAsset(event, 'cover', 0)} />
                          </Button>
                        </>
                      ) : form.contentType === 'Carrousel' ? (
                        <Button component="label" variant="outlined" startIcon={uploading ? <CircularProgress size={16} /> : <FileUploadOutlinedIcon />} disabled={uploading || !campaignId}>
                          Imágenes
                          <input hidden multiple type="file" accept="image/*" onChange={(event) => handleUploadAsset(event, 'carousel_slide', (form.assets || []).length)} />
                        </Button>
                      ) : (
                        <Button component="label" variant="outlined" startIcon={uploading ? <CircularProgress size={16} /> : <FileUploadOutlinedIcon />} disabled={uploading || !campaignId}>
                          Subir recurso
                          <input hidden type="file" accept="image/*,video/*,.pdf" onChange={(event) => handleUploadAsset(event, 'primary', 0)} />
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                  {imageSrc && (
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          position: 'relative',
                          height: 320,
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: '#111'
                        }}
                      >
                        <Cropper
                          image={imageSrc}
                          crop={crop}
                          zoom={zoom}
                          aspect={selectedAspect.ratio}
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onCropComplete={(croppedArea, pixels) => setCroppedAreaPixels(pixels)}
                        />
                      </Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setImageSrc('');
                            setCroppedAreaPixels(null);
                          }}
                        >
                          Cancelar recorte
                        </Button>
                        <Button size="small" variant="contained" onClick={handleUploadCroppedImage} disabled={uploading || !croppedAreaPixels}>
                          Usar recorte
                        </Button>
                      </Stack>
                    </Box>
                  )}
                  {(form.assets || []).length > 0 && (
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {(form.assets || []).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)).map((asset, index) => (
                        <Paper key={`${asset.s3Key}-${index}`} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 44, height: 44, borderRadius: 1, bgcolor: '#f3f4f6', overflow: 'hidden', flex: '0 0 44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {asset.mimeType?.startsWith('image/') ? (
                              <Box component="img" src={asset.previewUrl} alt={asset.role} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : asset.mimeType?.startsWith('video/') ? (
                              <Typography variant="caption">Video</Typography>
                            ) : (
                              <Typography variant="caption">PDF</Typography>
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={700}>{assetLabel(asset, index)}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>{asset.fileName || asset.s3Key}</Typography>
                          </Box>
                          <IconButton size="small" color="error" onClick={() => removeAssetFromForm(setForm, asset)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                  {getPrimaryAsset(form)?.previewUrl && form.contentType !== 'Carrousel' && (
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: selectedAspect.value === '9:16' ? 220 : selectedAspect.value === '1.91:1' ? 420 : 320,
                          aspectRatio: selectedAspect.value.replace(':', ' / '),
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: '#f6f7f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {getPrimaryAsset(form)?.mimeType?.startsWith('video/') ? (
                          <Box component="video" src={getPrimaryAsset(form).previewUrl} poster={getCoverAsset(form)?.previewUrl} controls sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : getPrimaryAsset(form)?.mimeType === 'application/pdf' ? (
                          <Typography variant="body2" color="text.secondary">PDF cargado</Typography>
                        ) : (
                          <Box component="img" src={getPrimaryAsset(form).previewUrl} alt={form.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth multiline minRows={5} label="Copy / contenido listo" value={form.copy || ''} onChange={(event) => updateField('copy', event.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth multiline minRows={2} label="Notas internas" value={form.notes || ''} onChange={(event) => updateField('notes', event.target.value)} />
              </Grid>
            </Grid>
          )}
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper' }}>
        {step === 0 && editing && <Button color="error" onClick={onDelete}>Eliminar</Button>}
        <Box sx={{ flex: 1 }} />
        {step === 0 && <Button onClick={onClose}>Cancelar</Button>}
        {step === 1 && <Button onClick={() => setStep(0)}>Atrás</Button>}
        {step === 0 ? (
          <Button variant="contained" onClick={() => setStep(1)} disabled={!form.title || !form.plannedDate}>Siguiente</Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={onSave} disabled={!form.title || !form.plannedDate || !canSaveContent || schedulingPublication}>Guardar</Button>
            {form.channel === 'Instagram' && (
              <Button
                variant="contained"
                color="success"
                startIcon={schedulingPublication ? <CircularProgress size={16} color="inherit" /> : <CheckCircleRoundedIcon />}
                onClick={onSchedule}
                disabled={!form.title || !form.plannedDate || !canSaveContent || schedulingPublication}
              >
                Listo para publicar
              </Button>
            )}
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

function normalizeItemForm(item) {
  const normalizedContentType = item.channel === 'Email Marketing'
    ? 'Email'
    : (instagramContentTypes.includes(item.contentType) ? item.contentType : 'Historia');
  return {
    plannedDate: item.plannedDate,
    time: item.time || '',
    channel: item.channel,
    contentType: normalizedContentType,
    title: item.title,
    objective: item.objective || '',
    copy: item.copy || '',
    notes: item.notes || '',
    status: getCalculatedStatus(item),
    assetKey: item.assetKey || '',
    assetAspectRatio: item.assetAspectRatio || '1:1',
    assetPreviewUrl: item.assetPreviewUrl || '',
    assetMimeType: item.assetMimeType || '',
    assets: normalizeAssets(item),
    emailTemplateId: item.emailTemplateId || '',
    emailListId: item.emailListId || '',
    emailSendId: item.emailSendId || null,
    scheduledAt: item.scheduledAt || null,
    publishedAt: item.publishedAt || null
  };
}

function toItemPayload(form) {
  const normalizedContentType = form.channel === 'Email Marketing'
    ? 'Email'
    : (instagramContentTypes.includes(form.contentType) ? form.contentType : 'Historia');
  return {
    plannedDate: form.plannedDate,
    time: form.time || '',
    channel: form.channel,
    contentType: normalizedContentType,
    title: form.title,
    objective: form.objective || '',
    copy: form.channel === 'Email Marketing' ? '' : (form.copy || ''),
    notes: form.notes || '',
    assetKey: form.channel === 'Email Marketing' ? '' : (form.assetKey || ''),
    assetAspectRatio: form.assetAspectRatio || '1:1',
    assets: form.channel === 'Email Marketing' ? [] : (form.assets || []).map((asset) => ({
      role: asset.role,
      mediaType: asset.mediaType,
      s3Key: asset.s3Key,
      mimeType: asset.mimeType,
      aspectRatio: asset.aspectRatio || form.assetAspectRatio || '1:1',
      sortOrder: asset.sortOrder || 0,
      fileName: asset.fileName || '',
      size: asset.size || null
    })),
    emailTemplateId: form.emailTemplateId ? Number(form.emailTemplateId) : null,
    emailListId: form.emailListId ? Number(form.emailListId) : null,
    emailSendId: form.emailSendId || null,
    scheduledAt: form.scheduledAt || null,
    publishedAt: form.publishedAt || null
  };
}

function defaultRuleTitle(rule) {
  if (rule.channel === 'Email Marketing') return 'Email marketing';
  return rule.contentType || 'Contenido planificado';
}

function extractEmailTemplates(response) {
  const data = response?.templates || response?.data?.templates || response?.data || response;
  return Array.isArray(data) ? data.map(unwrapEmailTemplate).filter(Boolean) : [];
}

function extractEmailLists(response) {
  const data = response?.data || response?.lists || response;
  return Array.isArray(data) ? data : [];
}

function unwrapEmailTemplate(template) {
  return template?.data?.template || template?.data || template?.template || template || null;
}

async function enrichEmailListsWithCounts(lists) {
  const results = await Promise.allSettled(
    lists.map(async (list) => {
      if (typeof list.contacts === 'number' && list.contacts > 0) {
        return { ...list, contactsCount: list.contacts };
      }

      const detail = await EmailListService.getListById(list.id);
      const contactsCount = Array.isArray(detail?.contacts)
        ? detail.contacts.length
        : (typeof detail?.contacts === 'number' ? detail.contacts : detail?.list?.contacts || 0);

      return { ...list, contactsCount };
    })
  );

  return results.map((result, index) => {
    if (result.status === 'fulfilled') return result.value;
    const fallbackCount = typeof lists[index]?.contacts === 'number' ? lists[index].contacts : 0;
    return { ...lists[index], contactsCount: fallbackCount };
  });
}

function normalizeAssets(item) {
  if (item.assets?.length) return item.assets;
  if (!item.assetKey) return [];
  return [{
    role: 'primary',
    mediaType: (item.assetMimeType || '').split('/')[0] || 'image',
    s3Key: item.assetKey,
    previewUrl: item.assetPreviewUrl,
    mimeType: item.assetMimeType || 'application/octet-stream',
    aspectRatio: item.assetAspectRatio || '1:1',
    sortOrder: 0
  }];
}

function uploadResponseToAsset(uploaded, file) {
  return {
    role: uploaded.role || 'primary',
    mediaType: uploaded.mediaType || (uploaded.mimeType || file.type || '').split('/')[0],
    s3Key: uploaded.assetKey,
    previewUrl: uploaded.previewUrl,
    mimeType: uploaded.mimeType || file.type || 'application/octet-stream',
    aspectRatio: uploaded.aspectRatio || '1:1',
    sortOrder: uploaded.sortOrder || 0,
    fileName: uploaded.fileName || file.name,
    size: uploaded.size || file.size
  };
}

function upsertAsset(assets, nextAsset) {
  if (nextAsset.role === 'carousel_slide') {
    return [...assets, nextAsset].map((asset, index) => (
      asset.role === 'carousel_slide' ? { ...asset, sortOrder: index } : asset
    ));
  }
  return [...assets.filter((asset) => asset.role !== nextAsset.role), nextAsset];
}

function removeAssetFromForm(setForm, targetAsset) {
  setForm((prev) => {
    const assets = (prev.assets || []).filter((asset) => asset.s3Key !== targetAsset.s3Key);
    const primary = assets.find((asset) => asset.role === 'primary');
    return {
      ...prev,
      assets,
      assetKey: primary?.s3Key || '',
      assetPreviewUrl: primary?.previewUrl || '',
      assetMimeType: primary?.mimeType || ''
    };
  });
}

function getPrimaryAsset(item) {
  const assets = normalizeAssets(item);
  if (item.contentType === 'Carrousel') return getCarouselAssets(item)[0];
  return assets.find((asset) => asset.role === 'primary') || assets[0];
}

function getCoverAsset(item) {
  return normalizeAssets(item).find((asset) => asset.role === 'cover');
}

function getCarouselAssets(item) {
  return normalizeAssets(item)
    .filter((asset) => asset.role === 'carousel_slide')
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
}

function assetSummary(form) {
  const assets = form.assets || [];
  if (form.contentType === 'Reel') {
    const hasVideo = assets.some((asset) => asset.role === 'primary');
    const hasCover = assets.some((asset) => asset.role === 'cover');
    if (hasVideo && hasCover) return 'Video y portada cargados.';
    if (hasVideo) return 'Video cargado. Falta portada.';
    return 'Subí el video del reel y una portada.';
  }
  if (form.contentType === 'Carrousel') {
    const count = getCarouselAssets(form).length;
    return count ? `${count} imagen${count === 1 ? '' : 'es'} cargada${count === 1 ? '' : 's'}.` : 'Subí varias imágenes para el carrousel.';
  }
  return getPrimaryAsset(form) ? 'Recurso cargado.' : 'Subí una imagen, video o PDF para esta pieza.';
}

function assetLabel(asset, index) {
  if (asset.role === 'primary') return asset.mediaType === 'video' ? 'Video principal' : 'Recurso principal';
  if (asset.role === 'cover') return 'Portada';
  if (asset.role === 'carousel_slide') return `Imagen ${index + 1}`;
  return 'Recurso';
}

function canPreviewItem(item) {
  if (item.channel === 'Email Marketing') return Boolean(item.emailTemplateId && item.emailListId);
  return Boolean(getPrimaryAsset(item)?.previewUrl);
}

function emailTemplateName(item, emailTemplateMap) {
  return emailTemplateMap?.get(String(item.emailTemplateId))?.name || 'Plantilla seleccionada';
}

function emailListName(item, emailListMap) {
  return emailListMap?.get(String(item.emailListId))?.name || 'Lista seleccionada';
}

function emailDisplayTitle(item, emailTemplateMap) {
  if (item.emailTemplateId) return emailTemplateName(item, emailTemplateMap);
  if (item.title && !instagramContentTypes.includes(item.title)) return item.title;
  return 'Email marketing';
}

function emailCardDescription(item, emailTemplateMap, emailListMap) {
  if (item.emailTemplateId && item.emailListId) return `${emailTemplateName(item, emailTemplateMap)} · ${emailListName(item, emailListMap)}`;
  if (item.emailTemplateId) return `${emailTemplateName(item, emailTemplateMap)} · falta lista`;
  if (item.emailListId) return `Falta plantilla · ${emailListName(item, emailListMap)}`;
  return 'Pendiente de plantilla y lista';
}

function getInstagramAspectOptions(contentType) {
  const values = instagramAspectRatiosByType[contentType] || instagramAspectRatiosByType.Post;
  return values
    .map((value) => instagramAspectRatios.find((option) => option.value === value))
    .filter(Boolean);
}

function instagramFormatHelp(contentType) {
  if (contentType === 'Historia' || contentType === 'Reel') {
    return 'Historia y Reel usan formato vertical 9:16. Lo dejamos fijo para evitar piezas con medidas incorrectas.';
  }
  if (contentType === 'Carrousel') {
    return 'Para carrousel usamos formatos de feed. Todas las imágenes deberían mantener el mismo formato.';
  }
  return 'Para posts de feed habilitamos solo los formatos más seguros: vertical 4:5 o cuadrado 1:1.';
}

function contentGuidance(contentType, channel = 'Instagram') {
  if (channel === 'Email Marketing') {
    return 'Email marketing: seleccioná una plantilla y una lista. El envío se programa desde Email Marketing con la fecha y hora de esta pieza.';
  }
  if (contentType === 'Reel') {
    return 'Reel: necesitás un video vertical y una portada. Recomendado 9:16, 1080x1920, 24-60 fps y duración de 3 a 90 segundos.';
  }
  if (contentType === 'Carrousel') {
    return 'Carrousel: podés subir varias imágenes. Mantené el mismo formato en todas para que el preview y la publicación se vean consistentes.';
  }
  if (contentType === 'Historia') {
    return 'Historia: recomendado 9:16 vertical. Si subís imagen, recortala en el formato Historia/Reel.';
  }
  return 'Post: recomendado 1:1, 4:5 o 1.91:1 según el formato que quieras publicar.';
}

function publicationScheduleLabel(item) {
  if (item.publishedAt) return `Publicado el ${formatDateTimeLabel(item.publishedAt)}`;
  if (item.scheduledAt) return `Programado para ${formatDateTimeLabel(item.scheduledAt)}`;
  return `Se publicará el ${formatLongDate(item.plannedDate)}${item.time ? ` a las ${item.time}` : ''}`;
}

function formatDateTimeLabel(value) {
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getDate()} de ${monthNames[date.getMonth()]} ${date.getFullYear()} a las ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function getVideoMetadata(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight
      });
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer el video'));
    };
    video.src = url;
  });
}

function connectionStatusLabel(status) {
  if (status === 'connected') return 'Conectado';
  if (status === 'needs_configuration') return 'Falta configuración';
  if (status === 'expired') return 'Token vencido';
  return 'Desconectado';
}

function getRequestErrorMessage(err, fallback) {
  return err?.response?.data?.errorMessage
    || err?.response?.data?.error_message
    || err?.response?.data?.message
    || err?.message
    || fallback;
}

function getStats(items) {
  const total = items.length;
  const done = items.filter((item) => ['Programado', 'Publicado'].includes(getCalculatedStatus(item))).length;
  return { total, done, percent: total ? Math.round((done / total) * 100) : 0 };
}

function getInstagramFeedPreviewItems(campaigns) {
  return (campaigns || [])
    .flatMap((campaign) => (campaign.items || []).map((item) => ({ ...item, campaignName: campaign.name })))
    .filter((item) => item.channel === 'Instagram')
    .filter((item) => !isMissingContent(item) && Boolean(getPrimaryAsset(item)?.previewUrl))
    .sort((a, b) => {
      const dateCompare = parseDate(b.plannedDate).getTime() - parseDate(a.plannedDate).getTime();
      if (dateCompare !== 0) return dateCompare;
      return (b.time || '').localeCompare(a.time || '');
    });
}

function getTimelineStats(campaign) {
  if (!campaign?.startDate || !campaign?.endDate) return { percent: 0, detail: 'Sin fechas definidas' };
  const start = parseDate(campaign.startDate);
  const end = parseDate(campaign.endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const totalDays = Math.max(1, Math.ceil((end - start) / 86400000) + 1);
  const elapsedDays = Math.min(totalDays, Math.max(0, Math.floor((today - start) / 86400000) + 1));
  const percent = Math.round((elapsedDays / totalDays) * 100);

  if (today < start) return { percent: 0, detail: `Empieza el ${formatDate(campaign.startDate)}` };
  if (today > end) return { percent: 100, detail: `Finalizó el ${formatDate(campaign.endDate)}` };
  return { percent, detail: `${elapsedDays} de ${totalDays} días transcurridos` };
}

function statusColor(status) {
  if (status === 'Publicado') return 'success';
  if (status === 'Programado') return 'info';
  return 'error';
}

function isMissingContent(item) {
  if (item.channel === 'Email Marketing') return !item.emailTemplateId || !item.emailListId;
  if (item.contentType === 'Reel') return !getPrimaryAsset(item) || !getCoverAsset(item);
  if (item.contentType === 'Carrousel') return getCarouselAssets(item).length === 0;
  return !getPrimaryAsset(item) && !item.copy;
}

function getCalculatedStatus(item) {
  if (isMissingContent(item)) return 'Pendiente';
  return getPublicationStatus(item);
}

function toScheduledAt(date, time) {
  return `${date}T${time || '09:00'}:00`;
}

function sortByTime(a, b) {
  return (a.time || '99:99').localeCompare(b.time || '99:99');
}

function sortByDateTime(a, b) {
  return `${a.plannedDate} ${a.time || '99:99'}`.localeCompare(`${b.plannedDate} ${b.time || '99:99'}`);
}

function parseDate(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toDateInput(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function addDays(date, amount) {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + amount);
  return copy;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfWeek(date) {
  const copy = new Date(date);
  const offset = (copy.getDay() + 6) % 7;
  copy.setDate(copy.getDate() - offset);
  return copy;
}

function formatDate(value) {
  const date = parseDate(value);
  return `${date.getDate()} ${monthNames[date.getMonth()].slice(0, 3)} ${date.getFullYear()}`;
}

function formatLongDate(value) {
  const date = parseDate(value);
  return `${date.getDate()} de ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
}

export default Planning;
